import React, { useState } from 'react';
import { X, Users, FileText, Mail, Check, Clock } from 'lucide-react';
import { formatDate } from '../../../utils/formatters';

interface Participant {
  id: string;
  name: string;
  email: string;
  role: 'host' | 'co-host' | 'participant';
  department: string;
  joinTime: string;
  leaveTime: string;
}

interface MeetingSummary {
  keyPoints: string[];
  actionItems: Array<{
    task: string;
    assignee: string;
    deadline: string;
  }>;
  decisions: string[];
  followUps: Array<{
    task: string;
    owner: string;
    dueDate: string;
  }>;
  resources: Array<{
    title: string;
    url: string;
  }>;
}

interface RecordingDetailsProps {
  recordingId: string;
  onClose: () => void;
}

export default function RecordingDetails({ recordingId, onClose }: RecordingDetailsProps) {
  const [emailSent, setEmailSent] = useState(false);
  
  // Sample data - In real app, fetch from API
  const participants: Participant[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      email: 'sarah@company.com',
      role: 'host',
      department: 'M&A Team',
      joinTime: '2024-03-15T10:00:00Z',
      leaveTime: '2024-03-15T11:00:00Z'
    },
    {
      id: '2',
      name: 'Michael Chen',
      email: 'michael@company.com',
      role: 'co-host',
      department: 'Financial Analysis',
      joinTime: '2024-03-15T10:02:00Z',
      leaveTime: '2024-03-15T11:00:00Z'
    }
  ];

  const summary: MeetingSummary = {
    keyPoints: [
      'Reviewed target company financial statements',
      'Discussed synergy opportunities in operations',
      'Identified potential regulatory challenges'
    ],
    actionItems: [
      {
        task: 'Update valuation model with new projections',
        assignee: 'Michael Chen',
        deadline: '2024-03-20'
      },
      {
        task: 'Schedule regulatory compliance review',
        assignee: 'Emily Brown',
        deadline: '2024-03-22'
      }
    ],
    decisions: [
      'Proceed with phase 2 due diligence',
      'Engage external consultants for IT systems assessment',
      'Revise initial offer based on working capital analysis'
    ],
    followUps: [
      {
        task: 'Follow up on outstanding due diligence items',
        owner: 'Sarah Johnson',
        dueDate: '2024-03-18'
      }
    ],
    resources: [
      {
        title: 'Updated Financial Model',
        url: 'https://docs.example.com/model-v2'
      },
      {
        title: 'Due Diligence Checklist',
        url: 'https://docs.example.com/checklist'
      }
    ]
  };

  const handleSendSummary = () => {
    // In real app, integrate with email service
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Meeting Recording Details</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            {/* Attendance */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Attendance
              </h4>
              <div className="space-y-4">
                {participants.map(participant => (
                  <div key={participant.id} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{participant.name}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        participant.role === 'host' 
                          ? 'bg-blue-100 text-blue-800'
                          : participant.role === 'co-host'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {participant.role}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>{participant.email}</p>
                      <p>{participant.department}</p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDate(participant.joinTime)} - {formatDate(participant.leaveTime)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Summary */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                AI-Generated Summary
              </h4>
              <div className="space-y-4">
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Key Discussion Points</h5>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600">
                    {summary.keyPoints.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Action Items</h5>
                  <div className="space-y-2">
                    {summary.actionItems.map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{item.task}</span>
                        <div className="text-right">
                          <p className="text-gray-900">{item.assignee}</p>
                          <p className="text-xs text-gray-500">Due: {formatDate(item.deadline)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Key Decisions</h5>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600">
                    {summary.decisions.map((decision, index) => (
                      <li key={index}>{decision}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Shared Resources</h5>
                  <div className="space-y-2">
                    {summary.resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-blue-600 hover:text-blue-800"
                      >
                        {resource.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Distribution */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-900">Email Distribution</span>
              </div>
              <button
                onClick={handleSendSummary}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                {emailSent ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Sent!
                  </>
                ) : (
                  'Send Summary'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}